<template>
  <ModalAiConsent
    v-model:show="showConsentModal"
    @ai-terms-agreement-agreed="generate"
  />
  <WdsTooltip
    top
    :text="
      $t(
        'Uses AI to fill in seniority, skills, and industry based on the job title and description. Requires a title and description to be filled in first. Fields the AI has no data for are left unchanged.',
      )
    "
  >
    <WdsButton
      ref="triggerBtn"
      secondary
      :disabled="isGenerating || missingRequiredFields"
      @click="handleButtonClick"
    >
      <WdsStack align-center justify-center>
        <img v-if="isGenerating" src="/gifs/worksome-assistant-thinking.gif" style="height: 16px" />
        <WdsIcon v-else :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" :size="14" />
        <div class="m-l-5">
          {{ isGenerating ? $t('Generating...') : $t('Auto-fill from Overview') }}
        </div>
      </WdsStack>
    </WdsButton>
  </WdsTooltip>
</template>

<script setup lang="ts">
import { WdsButton, WdsStack, WdsIcon, WdsIconNameEnum, WdsTooltip } from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useTranslation } from '@/composables/translation'
import { ExpectedExperienceLevel, Industry } from '@/gql/graphql'
import ModalAiConsent from '@/components/LLM/ModalAiConsent.vue'
import { useAiConsent } from '@/composables/useAiConsent'
import { useToast } from '@/composables/useToast'
import api from '@/api/api-ai'

const { $t } = useTranslation()
const toast = useToast()
const isGenerating = ref(false)
const emit = defineEmits(['generate-metadata'])

const { needsConsent, showConsentModal } = useAiConsent()

const props = defineProps({
  jobId: {
    type: String,
    required: false,
    default: null,
  },
  title: {
    type: String,
    required: true,
    default: null,
  },
  description: {
    type: String,
    required: true,
    default: null,
  },
})

const missingRequiredFields = computed(
  () => !props.jobId || !props.title?.trim() || !props.description?.trim(),
)

export type GeneratedJobMetadataResponse = {
  seniority: ExpectedExperienceLevel[]
  skills: string[]
  industries: Industry[]
}

const generate = async () => {
  if (missingRequiredFields.value) return

  try {
    isGenerating.value = true

    const response = await api.generateJobMetadata(props.jobId, props.title, props.description)

    if (response.data) {
      emit('generate-metadata', {
        ...response.data,
        industries: response.data.industries.map(
          (industry: { id: string; name: string }) =>
            ({
              id: industry.id,
              name: industry.name,
            }) as Industry,
        ),
      } as GeneratedJobMetadataResponse)
    }
  } catch {
    toast.error($t('An error occurred, please try again.'))
  } finally {
    isGenerating.value = false
  }
}

const handleButtonClick = () => {
  if (needsConsent.value) {
    showConsentModal.value = true
  } else {
    generate()
  }
}
</script>
